import React from "react";

/**
 * Sic Bo bet table in the reference admin's markup — rendered inside the page's `.casino-detail`:
 *   .d-none.d-xl-block : .sicbo-top (3 title pills) + .sicbo-middle (Small | 18-col totals row +
 *                        singles/doubles/triples | Big) + .sicbo-bottom (15 two-dice combinations)
 *   .d-xl-none         : the reference's narrow-screen layout (top / left doubles+triples /
 *                        right totals+two dice+singles)
 * Styled by the ported reference rules in styles/index.css (+ styles/games/sicbo.css).
 *
 * Markets are read like the reference, by index into `sub` (same order as the feed):
 *   0 Small, 1 Big, 2 Any Triple, 3 Odd, 4 Even, 5-10 Double 1..6, 11-16 Triple 1..6,
 *   17-30 Total 4..17, 31-45 Combination (1,2)..(5,6), 46-51 Single 1..6.
 * The reference's suspended bindings are kept as-is (e.g. desktop Small follows sub[3]).
 */
const DICE = `${import.meta.env.BASE_URL}assets/img/dice`;
const DIE = [1, 2, 3, 4, 5, 6];
const PAIRS = [
  [1, 2], [1, 3], [1, 4], [1, 5], [1, 6],
  [2, 3], [2, 4], [2, 5], [2, 6],
  [3, 4], [3, 5], [3, 6],
  [4, 5], [4, 6], [5, 6],
];

function Dice({ n }) {
  return <img src={`${DICE}/dice${n}.png`} alt={`Dice ${n}`} />;
}

/** Children separated by " " text nodes, like Vue's `_v(" ")` between siblings. */
function spaced(nodes) {
  return nodes.map((node, i) => (
    <React.Fragment key={i}>
      {i > 0 && " "}
      {node}
    </React.Fragment>
  ));
}

export default function BetTableSicbo({ data = [], onBetClick }) {
  const sub = Array.isArray(data) ? data : [];
  const m = (i) => sub[i] || {};
  const isSuspended = (i) => m(i).gstatus !== "OPEN";
  const odds = (i) => m(i).b;
  const cls = (base, i) => `${base}${isSuspended(i) ? " suspended" : ""}`;
  const click = (i) => () => {
    if (isSuspended(i) || !sub[i]) return;
    onBetClick?.(sub[i]);
  };

  // Clickable market box. `si` = index whose gstatus drives .suspended, `bi` = market clicked.
  const box = (key, base, si, children, bi = si) => (
    <div key={key} className={cls(base, si)} onClick={click(bi)}>
      {children}
    </div>
  );

  const topBox = (key, label, value, si, extra = "", bi = si) =>
    box(
      key,
      `sicbo-middle-top-box sicbo-square-box${extra}`,
      si,
      value != null ? spaced([<div key="l">{label}</div>, <div key="v" className="sicbo-box-value">{value}</div>]) : <div>{label}</div>,
      bi
    );

  const total = (n) => topBox(`t${n}`, String(n), `${odds(13 + n)}:1`, 13 + n);

  const cube = (key, variant, si, dice) =>
    box(key, `sicbo-cube-box sicbo-square-box sicbo-cube-${variant}`, si, spaced(dice.map((n, j) => <Dice key={j} n={n} />)));

  const singles = DIE.map((n) => cube(`s${n}`, "single", 45 + n, [n]));
  const doubles = DIE.map((n) => cube(`d${n}`, "double", 4 + n, [n, n]));
  const triples = DIE.map((n) => cube(`tr${n}`, "tripple", 10 + n, [n, n, n]));
  const combos = PAIRS.map(([a, b], j) => cube(`c${j}`, "combination", 31 + j, [a, b]));

  const container = (key, title, items) => (
    <div key={key} className="sicbo-cube-box-container">
      <div className="sicbo-top-box sicbo-title-box">{title}</div>{" "}
      <div className="sicbo-cube-box-group">{spaced(items)}</div>
    </div>
  );
  const singlesTitle = spaced([
    <span key="1">1:1 On Single</span>,
    <span key="2">2:1 On Double</span>,
    <span key="3">3:1 On Triple</span>,
  ]);

  const lose = (i) => `${odds(i)}:1 Lose if Any Triple`;

  return (
    <>
      <div className="d-none d-xl-block">
        <div className="sicbo-top">
          <div className="sicbo-top-box sicbo-title-box">{lose(0)}</div>{" "}
          <div className="sicbo-top-box sicbo-title-box">{`${odds(2)}:1`}</div>{" "}
          <div className="sicbo-top-box sicbo-title-box">{lose(1)}</div>
        </div>{" "}
        <div className="sicbo-middle">
          {box("small", "sicbo-middle-small sicbo-square-box", 3, spaced([<div key="l">Small</div>, <div key="v" className="sicbo-box-value">4-10</div>]), 0)}{" "}
          <div className="sicbo-middle-midle">
            <div className="sicbo-middle-top-row">
              {spaced([
                topBox("odd", "ODD", `${odds(3)}:1`, 3, " sicbo-middle-top-box-odd"),
                ...[4, 5, 6, 7, 8, 9, 10].map(total),
                topBox("any", "Any Triple", null, 2, " sicbo-middle-top-box-odd"),
                ...[11, 12, 13, 14, 15, 16, 17].map(total),
                topBox("even", "Even", `${odds(4)}:1`, 4, " sicbo-middle-top-box-odd"),
              ])}
            </div>{" "}
            <div className="sicbo-middle-middle-row">
              {spaced([
                container("s", singlesTitle, singles),
                container("d", `${odds(5)}:1 Each Double`, doubles),
                container("t", `${odds(11)}:1 Each Triple`, triples),
              ])}
            </div>
          </div>{" "}
          {box("big", "sicbo-middle-big sicbo-square-box", 1, spaced([<div key="l">Big</div>, <div key="v" className="sicbo-box-value">11-17</div>]))}
        </div>{" "}
        <div className="sicbo-bottom">{container("c", `${odds(31)}:1 Two Dice`, combos)}</div>
      </div>{" "}
      <div className="d-xl-none">
        <div className="sicbo-top">
          {spaced([
            container("l", lose(0), [
              topBox("msmall", "SMALL", "4-10", 0, " sicbo-middle-top-box-odd"),
              topBox("modd", "ODD", `${odds(3)}:1`, 3, " sicbo-middle-top-box-odd"),
            ]),
            container("a", `${odds(2)}:1`, [topBox("many", "Any Triple", null, 2, " sicbo-middle-top-box-odd")]),
            container("r", `${odds(4)}:1 Lose if Any Triple`, [
              topBox("meven", "EVEN", `${odds(4)}:1`, 4, " sicbo-middle-top-box-odd"),
              topBox("mbig", "BIG", "11-17", 1, " sicbo-middle-top-box-odd"),
            ]),
          ])}
        </div>{" "}
        <div className="sicbo-middle">
          <div className="sicbo-middle-left">
            {container("d", `${odds(5)}:1 Each Double`, doubles)}{" "}
            {container("t", `${odds(11)}:1 Each Triple`, triples)}
          </div>{" "}
          <div className="sicbo-middle-right">
            <div className="sicbo-middle-top-row">{spaced([4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map(total))}</div>{" "}
            <div className="sicbo-bottom">{container("c", `${odds(31)}:1 Two Dice`, combos)}</div>{" "}
            <div className="sicbo-middle-middle-row">{container("s", singlesTitle, singles)}</div>
          </div>
        </div>
      </div>
    </>
  );
}
